<template>
  <div class="remove-personnel">
    <p>Är du säker på att du vill ta bort {{ personName }} som personal?</p>

    <div class="remove-personnel-notice">
      <h4>OBS</h4>
      <p>För att ansökningar inte ska glömmas bort kommer vi flytta över {{ userToBeDeleted }} ansökningar till dig.</p>
    </div>

    <div class="remove-personnel-actions">
      <AppButton :disabled="isRemoving" @click="emit('close')">Avbryt</AppButton>
      <AppButton color="red" :loading="isRemoving" :disabled="isRemoving" @click="confirm">Ta bort</AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { AppButton, useNotifications } from '@staff/ui'
import { formatName } from '@staff/shared'

import { deletePersonnel, type PersonnelListItem } from '@/services/personnel'

const props = defineProps<{
  person: PersonnelListItem
  reloadPersonnel: () => void | Promise<void>
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const { notify } = useNotifications()

const isRemoving = ref(false)

const personName = computed(() => formatName(props.person.user) || 'personen')

// Swedish genitive: a name already ending in s, x or z takes no extra s ("Jonas ansökningar").
const userToBeDeleted = computed(() => {
  return /[sxz]$/i.test(personName.value) ? personName.value : `${personName.value}s`
})

const confirm = async () => {
  isRemoving.value = true

  try {
    await deletePersonnel(props.person.id)
  } catch (error) {
    notify({
      type: 'error',
      title: `Kunde inte ta bort ${personName.value}`,
      description: error instanceof Error ? error.message : String(error),
    })

    emit('close')
    return
  } finally {
    isRemoving.value = false
  }

  notify({ type: 'success', title: `${personName.value} har tagits bort som personal` })

  await props.reloadPersonnel()
  emit('close')
}
</script>

<style scoped lang="scss">
.remove-personnel {
  display: grid;
  gap: var(--space-16);
}

.remove-personnel-notice {
  display: grid;
  gap: var(--space-4);
}

.remove-personnel-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
}
</style>
